import { FACEBOOK_TOKEN_INVALID } from '@mm/constants/error-codes';
import {
  CONNECTVITIY_ERROR_MODAL_TEXT,
  CONNECTVITIY_ERROR_MODAL_TITLE,
  NETWORK_ERROR_MODAL_TEXT,
  NETWORK_ERROR_MODAL_TITLE,
} from '@mm/constants/error-descriptions';
import { ALL_PERMISSIONS } from '@mm/constants/facebook-permissions';
import {
  NOT_ACCEPTABLE,
  NOT_FOUND,
  UNAUTHORIZED,
  UNPROCESSABLE_ENTITY,
} from '@mm/constants/status-codes';
import { ErrorReportingUtils } from '@mm/plugins';
import { getErrorJson } from '@mm/utils';
import { includes } from 'lodash';
import { Dispatch } from 'redux';
import { reset, unbusyAll } from '../actions/commonActions';
import ConnectedPageCRUDActions from '../actions/ConnectedPageCRUDActions';
import CrudActionContainer from '../actions/CrudActionContainer';
import { hideAllModals } from '../actions/networkActions';
import sessionCRUDActions from '../actions/SessionCRUDActions';
import { hideSlideoutMenu } from '../actions/viewActions';
import AuthenticatedController from '../controllers/AuthenticatedController';
import modalActions from '../modules/modalsState/actions';
import { StoreState } from '../store-interfaces';

const BOT_IS_INACTIVE = 'The Bot is inactive';
const SEND_LIMIT_EXCEEDED = 'Facebook Page has exceeded send credits limit';
const SEND_LIMIT_REACHED =
  'Validation failed: One of your Facebook Pages has reached the send credits limit.';

// Strips id's from endpoints for better tagging in Sentry
const getEndpointIdentifier = (url: string) => {
  if (url) {
    const endpoint = url.replace(API_URL, '');
    const withoutQueryString = endpoint.replace(/\?.*$/, '');
    return withoutQueryString.replace(/\d+/g, ':id');
  }

  return '';
};

export type ResponseOrError = Response | TypeError | Error;

export const getErrorText = async (error: ResponseOrError): Promise<string> => {
  let text = '';

  if (error instanceof Response) {
    const responseError = await getErrorJson(error);

    if (error.status) {
      text = `(${error.status}) ${error.statusText} \n`;
      // on error 500 we get html and parsing json throws
      try {
        if (responseError && Array.isArray(responseError.errors)) {
          text += responseError.errors.join(' \n');
        }
      } catch (e) {
        text +=
          'Was not able to parse error message, likely server returned html';
      }
    }
  } else if (error.message) {
    text += error.toString();
  }

  return text;
};

const commonHandler =
  (dispatch: Dispatch<StoreState>) =>
  async (
    error: ResponseOrError,
    customUnprocessableErrorHandler?: (errorText?: string) => any,
  ) => {
    if (error instanceof Error && error.name === 'AbortError') {
      return error;
    }

    if (error instanceof Response) {
      const responseErrorBody = await getErrorJson(error);

      if (error.status === UNAUTHORIZED) {
        if (
          responseErrorBody &&
          responseErrorBody.error_code === FACEBOOK_TOKEN_INVALID
        ) {
          dispatch(reset());
          dispatch(sessionCRUDActions.clear());

          return responseErrorBody;
        }

        dispatch(
          modalActions.openPrettyErrorModal({
            title: 'Authentication Error',
            errorMessage: 'Sorry, for some reason you need to login again',
          }),
        );
        AuthenticatedController.clearSession();
        setTimeout(() => {
          dispatch(modalActions.closeModal());
          dispatch(hideAllModals());
          window.location.href = window.MMApp?.loginUrl || '/login';
        }, 3000);

        return responseErrorBody;
      }

      if (window.MMApp?.throwErrors) {
        throw error;
      }

      if (
        error.status === NOT_FOUND &&
        error.url.indexOf('/api/invite/') !== -1
      ) {
        return error;
      }

      if (error.status === NOT_ACCEPTABLE) {
        return error;
      }

      if (error.status === UNPROCESSABLE_ENTITY) {
        if (responseErrorBody.errors) {
          if (includes(responseErrorBody.errors[0], BOT_IS_INACTIVE)) {
            dispatch(unbusyAll());
            dispatch(modalActions.openActivateInactiveBotModal());
            return error;
          }

          if (
            responseErrorBody.errors[0] === SEND_LIMIT_REACHED ||
            responseErrorBody.errors[0] === SEND_LIMIT_EXCEEDED
          ) {
            dispatch(unbusyAll());
            dispatch(modalActions.openMonthlySendsWarningModal());
            throw responseErrorBody.errors[0];
          }

          if (customUnprocessableErrorHandler) {
            return customUnprocessableErrorHandler(responseErrorBody.errors[0]);
          }
        }

        const errorText = responseErrorBody && responseErrorBody.errors;
        dispatch(
          modalActions.openPrettyErrorModal({
            errorMessage: NETWORK_ERROR_MODAL_TEXT,
            title: NETWORK_ERROR_MODAL_TITLE,
            detailsForNerds: errorText,
          }),
        );

        // Note. I'm rethrowing here, so that the component of interest
        // could handle the failure as needed and not simply pretend everything is fine.
        throw errorText;
      }

      const ENDPOINT = getEndpointIdentifier(error.url);

      ErrorReportingUtils.reportError('API Request Failure', {
        extraData: {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          responseErrorBody,
        },
        tags: {
          ENDPOINT,
          status: error.status,
          statusText: error.statusText,
        },
      });
    } else if (error instanceof TypeError) {
      if (window.MMApp?.throwErrors) {
        throw error;
      }

      switch (error.message) {
        case 'Failed to fetch': // Chrome, Edge
        case 'NetworkError when attempting to fetch resource.': // FF
          dispatch(
            modalActions.openPrettyErrorModal({
              errorMessage: CONNECTVITIY_ERROR_MODAL_TEXT,
              title: CONNECTVITIY_ERROR_MODAL_TITLE,
              detailsForNerds: error.toString(),
            }),
          );
          return;
        default:
          ErrorReportingUtils.captureException(error);
      }
    } else {
      ErrorReportingUtils.captureException(error);
    }
    if (window.MMApp?.throwErrors) {
      throw error;
    }

    const text = await getErrorText(error);
    dispatch(
      modalActions.openPrettyErrorModal({
        errorMessage: NETWORK_ERROR_MODAL_TEXT,
        title: NETWORK_ERROR_MODAL_TITLE,
        detailsForNerds: text,
      }),
    );
  };

export const permissionsHandler =
  (dispatch: Dispatch<StoreState>, container: CrudActionContainer) =>
  async (
    error: ResponseOrError,
    allowManualFlow?: boolean,
    headerText?: string,
    bodyText?: string,
  ) => {
    if (error instanceof Response) {
      const res = await getErrorJson(error);
      const onCloseCallback = () => {
        dispatch(ConnectedPageCRUDActions.retrieveAvailablePages());
        dispatch(hideSlideoutMenu());
      };
      const requiredPermissions =
        res && res.required_permissions
          ? res.required_permissions
          : ALL_PERMISSIONS;

      switch (error.status) {
        case 403:
          dispatch(
            modalActions.openGrantPermissionsModal(
              requiredPermissions,
              onCloseCallback,
              allowManualFlow,
              headerText,
              bodyText,
            ),
          );
          dispatch(container.unbusy());
          break;
        default:
          return commonHandler(dispatch)(error);
      }
    } else {
      return commonHandler(dispatch)(error);
    }
  };

export default commonHandler;
